import CT from '@marketplace-ai/tags';
import { LoadingSpinner, Table } from '~/components';
import type { ListingContract } from '~/types/hlx/msaasDetail';
import { b2bSideCopy, type ListingSide } from '../../b2bSideCopy';
import {
  HOLIDAY_ROW_DATA_KEYS,
  WEEKEND_ROW_DATA_KEYS,
} from '../msaas-listing/wizard-steps/cost-calculator/consts';
import { generateTableColumns } from '../msaas-listing/wizard-steps/cost-calculator/tableCols';
import {
  calculateCostPerHour,
  calculateCostPerShift,
  calculatePricePerRVU,
  calculateRvusPerShift,
  checkIfShiftDetails,
  generateRowData,
} from '../msaas-listing/wizard-steps/cost-calculator/utils';
import { CostBreakdown } from './CostBreakdown';
import { CostCalculatorFailureNotice } from './CostCalculatorFailureNotice';
import { formatCalculatedMoney, hasValidCostCalculation } from './costCalculatorMoney';

const TAGS = CT.employer.msaasListings.costCalculator;

const accessibleValue = (label: string, value: number | null | undefined) =>
  value == null ? { 'aria-label': `${label}: Unavailable` } : {};

export const CostCalculator = ({
  footer,
  detail,
  loading,
  showHeading = true,
  failureAudience = 'employer',
  side,
}: {
  footer?: React.ReactNode;
  detail: ListingContract | undefined;
  loading: boolean;
  showHeading?: boolean;
  failureAudience?: 'employer' | 'provider';
  /** [MCV-1889] A selling listing prices what it offers; left out, the listing is a buying one. */
  side?: ListingSide;
}) => {
  const copy = b2bSideCopy(side);
  if (loading) return <LoadingSpinner />;

  if (!detail?.id) {
    return (
      <>
        {showHeading && <h3 className="sub-heading-sb mb-3 mt-8">{copy.costAnalysisHeading}</h3>}
        <p className="body-text text-grey-300">
          There are no compensation details to calculate from yet. Go back and complete the
          Compensation and Labor Metrics steps, then return here.
        </p>
        {footer}
      </>
    );
  }

  const hasValidCalculation = hasValidCostCalculation(detail);
  const hasRenderableBaseRate =
    detail.pricePerHour != null && Number.isFinite(Number(detail.pricePerHour));
  if (!hasValidCalculation && (failureAudience === 'employer' || !hasRenderableBaseRate)) {
    return (
      <>
        {showHeading && <h3 className="sub-heading-sb mb-3 mt-8">{copy.costAnalysisHeading}</h3>}
        <CostCalculatorFailureNotice audience={failureAudience} />
        {footer}
      </>
    );
  }

  const hasShiftDetails = checkIfShiftDetails(detail);
  const dayShiftCount = Number(detail.numDayShifts ?? 0);
  const dayHourlyRate = calculateCostPerHour(detail, 0);
  const dayCostPerShift = calculateCostPerShift(detail, dayHourlyRate);
  // The Day row has no differential, so it bypasses the configurable row helper.
  const weekdayRowData = [
    {
      id: detail.id,
      shiftType: 'Day',
      count: dayShiftCount,
      rvus: dayShiftCount * calculateRvusPerShift(detail),
      hourlyRate: dayHourlyRate,
      pricePerRvu: calculatePricePerRVU(detail, 0, dayHourlyRate),
      baseRate: dayHourlyRate,
      differential: 0,
      totalRate: dayHourlyRate,
      hoursPerShift: detail.hoursPerShift,
      costPerShift: dayCostPerShift,
      subTotal: dayCostPerShift * dayShiftCount,
    },
    generateRowData(detail, {
      shiftType: 'Evening',
      diffKey: 'eveShiftRateDiff',
      countKey: 'numEveShifts',
      tableIdx: 0,
    }),
    generateRowData(detail, {
      shiftType: 'Night',
      diffKey: 'nightShiftRateDiff',
      countKey: 'numNightShifts',
      tableIdx: 0,
    }),
  ];
  const weekendRowData = WEEKEND_ROW_DATA_KEYS.map((rowData) => generateRowData(detail, rowData));
  const holidayRowData = HOLIDAY_ROW_DATA_KEYS.map((rowData) => generateRowData(detail, rowData));

  const derivedTotalContractValue = [weekdayRowData, weekendRowData, holidayRowData]
    .flat()
    .reduce((sum, row) => sum + row.subTotal, 0);

  const shiftCount = detail.numOfShifts;
  const persistedTotalContractValue =
    detail.totalContractValue == null || !Number.isFinite(Number(detail.totalContractValue))
      ? null
      : Number(detail.totalContractValue);
  const totalContractValue = hasShiftDetails
    ? derivedTotalContractValue
    : persistedTotalContractValue;
  const baseContractValue = hasShiftDetails
    ? shiftCount == null
      ? null
      : shiftCount * dayCostPerShift
    : persistedTotalContractValue;
  const diffCost =
    baseContractValue == null || totalContractValue == null
      ? null
      : totalContractValue - baseContractValue;
  const diffPercentage =
    baseContractValue == null || baseContractValue === 0 || diffCost == null
      ? null
      : (diffCost / baseContractValue) * 100;
  const averageCostPerShift =
    shiftCount == null || shiftCount === 0 || totalContractValue == null
      ? null
      : totalContractValue / shiftCount;

  return (
    <>
      {showHeading && <h3 className="sub-heading-sb mb-3 mt-8">{copy.costAnalysisHeading}</h3>}
      {!hasValidCalculation && (
        <div className="mb-4">
          <CostCalculatorFailureNotice audience={failureAudience} />
        </div>
      )}
      <p>
        Based on the base rate of {formatCalculatedMoney(detail.pricePerHour)} plus shift
        differential
      </p>
      {hasShiftDetails && (
        <>
          <h3 className="sub-heading my-3">Shift-by-Shift Breakdown</h3>
          <h3 className="sub-heading mb-5 mt-8">Weekdays</h3>
          <CostBreakdown label="Weekdays cost breakdown">
            <Table
              tags={TAGS}
              rowData={weekdayRowData}
              columns={generateTableColumns(0)}
              loading={false}
            />
          </CostBreakdown>
          <h3 className="sub-heading mb-5 mt-8">Weekends</h3>
          <CostBreakdown label="Weekends cost breakdown">
            <Table
              tags={TAGS}
              rowData={weekendRowData}
              columns={generateTableColumns(1)}
              loading={false}
            />
          </CostBreakdown>
          <h3 className="sub-heading mb-5 mt-8">Holidays</h3>
          <CostBreakdown label="Holidays cost breakdown">
            <Table
              tags={TAGS}
              rowData={holidayRowData}
              columns={generateTableColumns(2)}
              loading={false}
            />
          </CostBreakdown>
        </>
      )}
      <div className="mt-8 w-full rounded-lg bg-primary-t-900 p-4">
        <h3 className="sub-heading my-3">Total Contract Value Summary</h3>
        <div className="cost-summary-columns columns-1 [overflow-wrap:anywhere] sm:columns-2">
          <p className="body-text-sb mb-3">Base Compensation (without differentials)</p>
          <p
            className="body-text-sb mb-3"
            data-cy={TAGS.totalCostNoDiff}
            {...accessibleValue('Base Compensation without differentials', baseContractValue)}
          >
            {formatCalculatedMoney(baseContractValue)}
          </p>
          {baseContractValue != null && (
            <p className="body-text mb-3">
              ( {shiftCount} shift X {detail.hoursPerShift}hr X ${detail.pricePerHour}/hr )
            </p>
          )}
          <p className="body-text-sb mb-3">Additional Differential Costs</p>
          <p
            className="body-text-sb mb-3"
            data-cy={TAGS.diffCost}
            {...accessibleValue('Additional Differential Costs', diffCost)}
          >
            {formatCalculatedMoney(diffCost)}
          </p>
          <p className="body-text mb-3">
            {diffPercentage == null
              ? 'Unavailable'
              : `${diffPercentage.toFixed(2)}% above base rate`}
          </p>
        </div>
        <div className="my-4 w-full border-t border-grey-700" />
        <p className="body-text-sb mt-3">Total Compensation (with differentials)</p>
        <p className="mt-3">
          <b data-cy={TAGS.totalCostWithDiff}>{formatCalculatedMoney(totalContractValue)}</b>
        </p>
        <p className="body-text mt-3">
          <span {...accessibleValue(copy.averagePerShift, averageCostPerShift)}>
            {copy.averagePerShift}: {formatCalculatedMoney(averageCostPerShift)}
          </span>{' '}
          |{' '}
          <span {...accessibleValue(copy.averagePerHour, detail.avgHourlyRate)}>
            {copy.averagePerHour}: {formatCalculatedMoney(detail.avgHourlyRate)}
          </span>
        </p>
      </div>
      {footer}
    </>
  );
};
